// Timing for the multi-moderator claiming protocol.
//
// Every one of these is a wall-clock threshold that several browsers compare
// against the same server timestamps, so they live in one place: the
// verification harness in UPLOAD_TESTER asserts against these exact values, and
// a moderator running an older build with different numbers would judge its
// peers dead too early or too late.
//
// See docs/superpowers/specs/2026-09-04-multi-moderator-claiming-design.md

/** The local coordination loop: heartbeat, reap, claim. */
export const TICK_MS = 1000

/** How often presence is written. Slower than the tick — nothing needs a 1s
 *  resolution on someone else's free-slot count. */
export const HEARTBEAT_MS = 3000

/** No heartbeat for this long and a moderator is considered gone: its claims
 *  become available to everyone else. */
export const PEER_STALE_MS = 20000

/** Idle allowance on top of the clip's own length before a held video can be
 *  taken by someone else. */
export const CLAIM_IDLE_MS = 20000

/** Ceiling on CLAIM_IDLE_MS + clip length, so a video with a nonsense duration
 *  cannot be held indefinitely. */
export const CLAIM_TTL_MAX_MS = 60000

/**
 * Grace added before another moderator's claim counts as expired.
 *
 * Both sides are comparing a server timestamp against a locally corrected
 * clock, and the correction is only as good as the last round trip. Without a
 * margin, a moderator a second fast robs a colleague who is still watching —
 * the one failure mode that actually loses work, since the victim's play-through
 * is thrown away.
 */
export const CLAIM_EXPIRY_MARGIN_MS = 3000

/** A video available (unclaimed, matching nobody's grid) for longer than this
 *  is claimed regardless of fair share. This is the anti-starvation valve: it
 *  bounds how long any video can wait while some moderator has a free slot. */
export const STARVATION_MS = 8000

/** Fraction of a clip's trim window the playhead must cover before it can be
 *  judged. */
export const WATCHED_FRACTION = 0.95

/** Per-rank delay before issuing claims within a tick, so the least loaded
 *  moderator reaches Firestore first and collisions stay rare. */
export const CLAIM_STAGGER_MS = 120

/** A claim document whose video is no longer pending is garbage; it is swept
 *  once it is at least this old, by the lowest-sessionId moderator only. */
export const ORPHAN_SWEEP_MS = 5000

/** A moderator that dies runs no cleanup, so its presence document would sit
 *  there for good. Peers ignore it long before this — PEER_STALE_MS — but
 *  somebody has to actually delete it or the collection only ever grows. The
 *  margin over PEER_STALE_MS is deliberate: a browser that has merely been
 *  backgrounded should be able to come back and find its own document — and
 *  if it is swept anyway, its next heartbeat simply writes it again. */
export const PRESENCE_SWEEP_MS = PEER_STALE_MS * 3

/**
 * How long a claim on this video is good for.
 *
 * A moderator has to watch the clip through before deciding, so a flat idle
 * timeout would rob someone mid-view of a long video. The allowance is the
 * clip's own length plus a fixed idle margin.
 */
export function claimTtlMs(video) {
  const trimmed =
    video?.trimEnd != null && video?.trimStart != null
      ? video.trimEnd - video.trimStart
      : null
  const seconds = trimmed ?? video?.duration ?? 0
  const clipMs = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0
  return Math.min(CLAIM_IDLE_MS + clipMs, CLAIM_TTL_MAX_MS)
}

/** The playable window of a clip, in ms — what the watch gate measures against. */
export function clipWindowMs(video) {
  const trimmed =
    video?.trimEnd != null && video?.trimStart != null
      ? video.trimEnd - video.trimStart
      : null
  const seconds = trimmed ?? video?.duration ?? 0
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0
}

/**
 * How long a tile waits for a play-through before it unlocks anyway.
 *
 * Without this, a clip that will not play — a corrupt upload, a codec this
 * browser lacks, a stalled download — can never be decided by anyone, so it
 * circulates between moderators forever, and the queue never empties. The tile
 * says the playback could not be verified, and the moderator judges what they
 * can see.
 *
 * Deliberately shorter than the claim's own TTL: the moderator holding the clip
 * has to get the chance to decide it before a peer is entitled to take it.
 */
export function watchGraceMs(video) {
  const window = clipWindowMs(video)
  return Math.min(1.5 * window + 8000, claimTtlMs(video) - 5000)
}
